import { c as _c } from "react/compiler-runtime";
import { File, FileArchive, FileAudio, FileCode2, FileImage, FileSpreadsheet, FileText, FileVideo, Link, Presentation } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import type * as React from "react";
export type FileTypeKind = "archive" | "audio" | "code" | "document" | "file" | "image" | "pdf" | "presentation" | "spreadsheet" | "video";
const archiveExtensions = new Set(["zip", "rar", "7z", "gz", "tar"]);
const audioExtensions = new Set(["aac", "m4a", "mp3", "ogg", "wav"]);
const codeExtensions = new Set(["json", "js", "jsx", "ts", "tsx", "html", "css", "xml", "yaml", "yml"]);
const documentExtensions = new Set(["doc", "docx", "txt", "rtf"]);
const imageExtensions = new Set(["avif", "bmp", "gif", "heic", "jpeg", "jpg", "png", "svg", "tif", "tiff", "webp"]);
const presentationExtensions = new Set(["ppt", "pptx", "key"]);
const spreadsheetExtensions = new Set(["csv", "tsv", "xls", "xlsx"]);
const videoExtensions = new Set(["avi", "m4v", "mkv", "mov", "mp4", "webm"]);
function getExtension(name: string): string {
  const match = /\.([a-z0-9]+)(?:$|[?#])/i.exec(name);
  return match?.[1]?.toLowerCase() ?? "";
}
export function resolveFileTypeKind({
  name,
  type,
  mimetype
}: {
  name: string;
  type?: string | null;
  mimetype?: string | null;
}): FileTypeKind {
  const mime = (mimetype || type || "").toLowerCase().split(";", 1)[0]?.trim() ?? "";
  const extension = getExtension(name);
  if (mime.startsWith("image/") || imageExtensions.has(extension)) {
    return "image";
  }
  if (mime.startsWith("video/") || videoExtensions.has(extension)) {
    return "video";
  }
  if (mime.startsWith("audio/") || audioExtensions.has(extension)) {
    return "audio";
  }
  if (mime === "application/pdf" || extension === "pdf") {
    return "pdf";
  }
  if (mime.includes("spreadsheet") || mime.includes("excel") || mime === "text/csv" || spreadsheetExtensions.has(extension)) {
    return "spreadsheet";
  }
  if (mime.includes("presentation") || mime.includes("powerpoint") || presentationExtensions.has(extension)) {
    return "presentation";
  }
  if (mime.includes("zip") || mime.includes("compressed") || mime.includes("tar") || mime.includes("gzip") || archiveExtensions.has(extension)) {
    return "archive";
  }
  if (mime.includes("json") || mime.includes("javascript") || mime.includes("xml") || codeExtensions.has(extension)) {
    return "code";
  }
  if (mime.includes("word") || mime === "text/plain" || mime.startsWith("text/") || documentExtensions.has(extension)) {
    return "document";
  }
  return "file";
}
const displayExtensionByExtension: Record<string, string> = {
  jpeg: "JPEG",
  jpg: "JPEG",
  tif: "TIFF",
  tiff: "TIFF"
};
function getDisplayExtension(extension: string) {
  return displayExtensionByExtension[extension] ?? extension.toUpperCase();
}
export function resolveFileTypeLabel({
  name,
  type,
  mimetype
}: {
  name: string;
  type?: string | null;
  mimetype?: string | null;
}): string {
  const extension = getExtension(name);
  const displayExtension = extension ? getDisplayExtension(extension) : "";
  const kind = resolveFileTypeKind({
    name,
    type,
    mimetype
  });
  switch (kind) {
    case "archive":
      {
        return displayExtension ? `${displayExtension} archive` : "Archive";
      }
    case "audio":
      {
        return displayExtension ? `${displayExtension} audio` : "Audio";
      }
    case "code":
      {
        return displayExtension ? `${displayExtension} file` : "Code file";
      }
    case "document":
      {
        if (extension === "doc" || extension === "docx") return "Word document";
        if (extension === "txt") return "Text document";
        return "Document";
      }
    case "image":
      {
        return displayExtension ? `${displayExtension} image` : "Image";
      }
    case "pdf":
      {
        return "PDF";
      }
    case "presentation":
      {
        return extension === "ppt" || extension === "pptx" ? "PowerPoint" : "Presentation";
      }
    case "spreadsheet":
      {
        if (extension === "csv") return "CSV";
        if (extension === "xls" || extension === "xlsx") return "Excel spreadsheet";
        return "Spreadsheet";
      }
    case "video":
      {
        return displayExtension ? `${displayExtension} video` : "Video";
      }
    case "file":
    default:
      {
        return displayExtension && extension !== "bin" ? `${displayExtension} file` : "File";
      }
  }
}
const iconsByKind: Record<FileTypeKind, LucideIcon> = {
  archive: FileArchive,
  audio: FileAudio,
  code: FileCode2,
  document: FileText,
  file: File,
  image: FileImage,
  pdf: FileText,
  presentation: Presentation,
  spreadsheet: FileSpreadsheet,
  video: FileVideo
};

// A neutral file-kind tile: 28px with a 16px glyph, `lg` for 40/48px library
// rows, `fill` to cover a thumbnail slot.
const fileTypeIconVariants = cva("flex shrink-0 items-center justify-center bg-bg-primary text-text-secondary", {
  variants: {
    size: {
      md: "size-7 rounded-editor-block [&>svg]:size-4",
      lg: "size-10 rounded-editor-block sm:size-12 [&>svg]:size-5",
      fill: "size-full [&>svg]:size-5"
    }
  },
  defaultVariants: {
    size: "md"
  }
});
export function FileTypeIcon(t0) {
  const $ = _c(7);
  const {
    name,
    type,
    mimetype,
    size,
    link: t1
  } = t0;
  const link = t1 === undefined ? false : t1;
  const Icon = link ? Link : iconsByKind[resolveFileTypeKind({
    name,
    type,
    mimetype
  })];
  let t2;
  if ($[0] !== size) {
    t2 = fileTypeIconVariants({
      size
    });
    $[0] = size;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== Icon) {
    t3 = <Icon />;
    $[2] = Icon;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <span aria-hidden="true" className={t2}>{t3}</span>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}

// The frame a file preview (image or PDF page) layers over its FileTypeIcon:
// `row` matches the `lg` icon, `gallery` is a full-width square.
const fileThumbnailVariants = cva("relative flex shrink-0 overflow-hidden rounded-editor-block", {
  variants: {
    size: {
      row: "size-10 sm:size-12",
      gallery: "aspect-square w-full"
    }
  },
  defaultVariants: {
    size: "row"
  }
});
export function FileThumbnail(t0) {
  const $ = _c(5);
  const {
    size,
    children
  } = t0;
  let t1;
  if ($[0] !== size) {
    t1 = fileThumbnailVariants({
      size
    });
    $[0] = size;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <span className={t1}>{children}</span>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}